import { Link } from "@tanstack/react-router";
import { ListPlus, X } from "lucide-react";
import { useState } from "react";

import { useApi } from "@/api/hooks";
import { Button, Skeleton } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatNumber, formatPct, trendClass } from "@/lib/format";

/**
 * The watchlist docked beside every page (wide screens): the user's lists as
 * tabs, each symbol with its live price, refreshed every 2 s.
 */
export function WatchDock({ onClose }: { onClose: () => void }) {
  const lists = useApi("/me/watchlists");
  const [active, setActive] = useState<number | null>(null);
  const all = lists.data ?? [];
  const list = all.find((l) => l.id === active) ?? all[0];
  const symbols = list?.symbols ?? [];
  const quotes = useApi(
    "/live/symbols",
    { query: { symbols: symbols.join(",") } },
    { enabled: symbols.length > 0, refetchInterval: 2_000, staleTime: 2_000 },
  );
  const bySymbol = new Map((quotes.data ?? []).map((q) => [q.symbol, q]));

  return (
    <aside
      aria-label="Watchlist"
      className="flex h-full min-h-0 flex-col border-l border-border bg-surface"
    >
      <div className="flex h-11 shrink-0 items-center justify-between border-b border-border pr-1.5 pl-4">
        <h2 className="text-sm font-semibold">Watchlist</h2>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Hide watchlist"
          onClick={onClose}
        >
          <X />
        </Button>
      </div>
      {all.length > 1 ? (
        <div
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-2 py-1.5"
          role="tablist"
          aria-label="Lists"
        >
          {all.map((l) => (
            <button
              key={l.id}
              type="button"
              role="tab"
              aria-selected={l.id === list?.id}
              onClick={() => setActive(l.id)}
              className={cn(
                "h-7 shrink-0 rounded-md px-2.5 text-xs font-medium whitespace-nowrap",
                l.id === list?.id
                  ? "bg-brand-soft text-brand"
                  : "text-muted hover:bg-surface-2",
              )}
            >
              {l.name}
            </button>
          ))}
        </div>
      ) : null}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {lists.isPending ? (
          <div className="grid gap-2 p-4">
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
            <Skeleton className="h-8" />
          </div>
        ) : symbols.length === 0 ? (
          <div className="grid justify-items-start gap-2 p-4 text-sm text-muted">
            <p>No stocks in this list yet.</p>
            <Link
              to="/app/watchlist"
              className="inline-flex items-center gap-1.5 font-medium text-brand hover:underline"
            >
              <ListPlus className="size-4" aria-hidden /> Add stocks
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {symbols.map((s) => {
              const q = bySymbol.get(s.toUpperCase());
              return (
                <li key={s}>
                  <Link
                    to="/app/stocks/$symbol"
                    params={{ symbol: s }}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-surface-2"
                    activeProps={{ className: "bg-surface-2" }}
                  >
                    <span className="min-w-0 truncate text-sm font-medium">
                      {s}
                    </span>
                    <span className="num text-right text-sm">
                      <span className={cn("block", trendClass(q?.change_pct))}>
                        {q ? formatNumber(q.ltp, 2) : "–"}
                      </span>
                      <span
                        className={cn(
                          "block text-xs",
                          trendClass(q?.change_pct),
                        )}
                      >
                        {q ? formatPct(q.change_pct, 2) : ""}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Link
        to="/app/watchlist"
        className="shrink-0 border-t border-border px-4 py-2.5 text-xs font-medium text-brand hover:bg-surface-2"
      >
        Manage watchlists
      </Link>
    </aside>
  );
}
